import { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Calendar, DollarSign, Building, Download, CreditCard, MapPin, Receipt, Ticket, Plus, User, CheckCircle, AlertCircle, Eye, Monitor, Smartphone, Tablet, ChevronDown, Pencil, Trash2, X, Loader2, RotateCcw, RefreshCw } from 'lucide-react';
import { adminInvoicesAPI, adminRefundsAPI, invoicesAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import ApplyPaymentModal from '../../components/admin/ApplyPaymentModal';
import { useDisplay } from '../../hooks/useDisplay';
import { isPastDate } from '../../utils/dates';
import { formatDateTime as fmtDateTime, formatMoney } from '../../utils/format';

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  // Handle timezone issues for dates that come as UTC midnight
  // PostgreSQL returns dates as "2025-12-10T00:00:00.000Z" which shows as Dec 9 in EST
  let normalized = dateString;
  if (typeof dateString === 'string') {
    // Extract just the date part and add noon time to prevent rollback
    const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      normalized = dateMatch[1] + 'T12:00:00';
    }
  }
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return fmtDateTime(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrency(amount) {
  return formatMoney(amount || 0);
}

// What happened to the ConnectWise invoice payment when a refund was recorded.
const CW_REFUND_CHIPS = {
  reflected: { label: 'Reflected in ConnectWise', cls: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
  not_reflected: { label: 'Not reflected in ConnectWise', cls: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300' },
  failed: { label: 'ConnectWise update failed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
  not_allowed_closed: { label: 'Invoice closed in ConnectWise', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' },
  in_progress: { label: 'Updating ConnectWise…', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
};

/**
 * One refund, directly under the payment it refunds:
 * "Refund −$X · date · method ••1234 · gateway · by <user>" and a status chip.
 * A failed ConnectWise update offers Retry; one not reflected offers Update.
 */
function RefundLine({ refund, canAct, onUpdate, updating }) {
  const chip = refund.cwStatus ? CW_REFUND_CHIPS[refund.cwStatus] : null;
  const method = refund.last4 ? `${String(refund.methodType || '').match(/bank|ach/i) ? 'account' : 'card'} ••${refund.last4}` : null;
  const parts = [formatDateTime(refund.createdAt), method, refund.gatewayLabel, refund.createdByName ? `by ${refund.createdByName}` : null].filter(Boolean);
  const actionLabel = refund.cwStatus === 'failed' ? 'Retry ConnectWise update' : refund.cwStatus === 'not_reflected' ? 'Update ConnectWise' : null;
  return (
    <div className="mt-2 ml-11 pl-3 border-l-2 border-red-200 dark:border-red-800 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <RotateCcw className="w-3.5 h-3.5 text-red-500" />
        <span className="font-medium text-red-600 dark:text-red-400">Refund −{formatCurrency(refund.amount)}</span>
        <span className="text-neutral-500 dark:text-neutral-400">· {parts.join(' · ')}</span>
        {chip && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${chip.cls}`}>{chip.label}</span>}
        {canAct && actionLabel && (
          <button
            onClick={() => onUpdate(refund.id)}
            disabled={updating}
            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1 disabled:opacity-50"
          >
            {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {actionLabel}
          </button>
        )}
      </div>
      {refund.cwError && refund.cwStatus !== 'reflected' && (
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{refund.cwError}</p>
      )}
      {refund.cwStatus === 'not_allowed_closed' && !refund.cwError && (
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">Adjust this invoice&rsquo;s payment in ConnectWise; its API refuses payment changes on a closed invoice.</p>
      )}
    </div>
  );
}

export default function AdminInvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasMinRole } = useAuthStore();
  // Label renames apply on admin pages too (visibility overrides don't —
  // useDisplay always returns visible=true on /admin/ routes).
  const d = useDisplay();
  const [downloading, setDownloading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [editingCwPayment, setEditingCwPayment] = useState(null);
  const [deletingCwPayment, setDeletingCwPayment] = useState(null);
  const [editingBreakdown, setEditingBreakdown] = useState(null);
  const [deletingBreakdown, setDeletingBreakdown] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const statusDropdownRef = useRef(null);

  const canApplyPayments = hasMinRole('finance');

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-invoice', id],
    queryFn: () => adminInvoicesAPI.get(id),
  });

  const { data: viewsData } = useQuery({
    queryKey: ['invoice-views', id],
    queryFn: () => invoicesAPI.getViews(id),
    enabled: !!id,
  });

  const views = viewsData?.data?.views || [];
  const viewStats = viewsData?.data?.stats;

  const invoice = data?.data?.invoice;
  const cwPayments = data?.data?.cwPayments || [];
  const paymentBreakdown = data?.data?.paymentBreakdown || [];
  const refunds = data?.data?.refunds || [];
  const localPayments = data?.data?.localPayments || [];
  // Refunds of payments the breakdown does not hold (a card payment whose
  // refund was not reflected in ConnectWise), grouped under their payment.
  const refundsFor = (source, paymentId) =>
    source && paymentId ? refunds.filter((r) => r.source === source && r.paymentId === paymentId) : [];
  const orphanRefundGroups = (() => {
    const shown = new Set(paymentBreakdown.filter((b) => b.sourcePaymentId).map((b) => `${b.source}:${b.sourcePaymentId}`));
    const groups = new Map();
    for (const r of refunds) {
      const key = `${r.source}:${r.paymentId}`;
      if (shown.has(key)) continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    }
    return [...groups.values()];
  })();
  const downpaymentApplied = data?.data?.downpaymentApplied || 0;
  const remainingDownpayment = data?.data?.remainingDownpayment || 0;
  const creditsApplied = data?.data?.creditsApplied || 0;

  // Fetch billing statuses for dropdown
  const { data: statusesData } = useQuery({
    queryKey: ['billing-statuses'],
    queryFn: () => adminInvoicesAPI.getBillingStatuses(),
  });
  const billingStatuses = statusesData?.data?.statuses || [];

  // Mutation to update invoice status
  const updateStatusMutation = useMutation({
    mutationFn: ({ statusId }) => adminInvoicesAPI.updateStatus(id, statusId),
    onSuccess: async () => {
      setStatusError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-invoice', id], refetchType: 'all' });
      setShowStatusDropdown(false);
    },
    onError: (error) => {
      console.error('Invoice status update error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      const message = error.response?.data?.error || error.message || 'Failed to update status';
      console.error('Setting statusError to:', message);
      setStatusError(message);
      setShowStatusDropdown(false);
      // Auto-clear error after 10 seconds
      setTimeout(() => setStatusError(null), 10000);
    },
  });

  // Mutation to update CW payment
  const updateCwPaymentMutation = useMutation({
    mutationFn: ({ paymentId, data }) => adminInvoicesAPI.updatePayment(id, paymentId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-invoice', id], refetchType: 'all' });
      setEditingCwPayment(null);
    },
  });

  // Mutation to delete CW payment
  const deleteCwPaymentMutation = useMutation({
    mutationFn: (paymentId) => adminInvoicesAPI.deletePayment(id, paymentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-invoice', id], refetchType: 'all' });
      setDeletingCwPayment(null);
    },
  });

  // Mutation to update breakdown payment (syncs to CW automatically)
  const updateBreakdownMutation = useMutation({
    mutationFn: ({ breakdownId, data }) => adminInvoicesAPI.updatePaymentBreakdown(breakdownId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-invoice', id], refetchType: 'all' });
      setEditingBreakdown(null);
    },
  });

  // Mutation to delete breakdown payment (syncs to CW automatically)
  const [updatingRefundId, setUpdatingRefundId] = useState(null);
  const [refundCwError, setRefundCwError] = useState(null);
  const updateRefundCwMutation = useMutation({
    mutationFn: (refundId) => {
      setUpdatingRefundId(refundId);
      setRefundCwError(null);
      return adminRefundsAPI.updateConnectWise(refundId);
    },
    onSuccess: (res) => {
      const cw = res?.data?.connectwise;
      if (cw && cw.status !== 'reflected') setRefundCwError(cw.error || 'ConnectWise was not updated.');
      queryClient.invalidateQueries({ queryKey: ['admin-invoice', id] });
    },
    onError: (err) => setRefundCwError(err.response?.data?.error || 'ConnectWise was not updated.'),
    onSettled: () => setUpdatingRefundId(null),
  });

  const deleteBreakdownMutation = useMutation({
    mutationFn: (breakdownId) => adminInvoicesAPI.deletePaymentBreakdown(breakdownId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-invoice', id], refetchType: 'all' });
      setDeletingBreakdown(null);
    },
  });

  // Fetch credit memos for the company
  const { data: creditMemosData, isLoading: creditMemosLoading } = useQuery({
    queryKey: ['credit-memos', invoice?.company?.id],
    queryFn: () => adminInvoicesAPI.getCompanyCreditMemos(invoice?.company?.id),
    enabled: !!invoice?.company?.id && showCreditModal,
  });
  const creditMemos = creditMemosData?.data?.creditMemos || [];

  // Note: ConnectWise does not have an API for applying credit memos
  // Credits must be applied directly in ConnectWise (Finance → Special Invoices)

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await invoicesAPI.getPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice?.invoiceNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download invoice PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-invoice', id], refetchType: 'all' });
    setShowPaymentModal(false);
  };

  const getStatusBadge = () => {
    const balance = invoice?.balance || 0;
    const total = invoice?.total || 0;
    const isOverdue = invoice?.dueDate && isPastDate(invoice.dueDate) && balance > 0;

    if (balance <= 0) {
      return <span className="badge badge-success">Paid</span>;
    }
    if (isOverdue) {
      return <span className="badge badge-danger">Overdue</span>;
    }
    if (balance < total) {
      return <span className="badge badge-warning">Partial</span>;
    }
    return <span className="badge badge-info">Open</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !invoice) {
    const errorData = error?.response?.data;
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <p className="text-red-700 dark:text-red-300 font-medium">
          {errorData?.error || 'Invoice not found'}
        </p>
        {errorData?.details && (
          <p className="text-red-600 dark:text-red-400 text-sm mt-2">
            {errorData.details}
          </p>
        )}
        <button onClick={() => {
          const returnUrl = sessionStorage.getItem('adminInvoicesReturnUrl') || '/admin/invoices';
          sessionStorage.removeItem('adminInvoicesReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        }} className="btn-secondary btn-sm mt-4">
          Back to Invoices
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Error Toast */}
      {statusError && (
        <div className="fixed top-4 right-4 z-50 max-w-md p-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg shadow-lg animate-in slide-in-from-top">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-red-800 dark:text-red-200">Status Change Failed</p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{statusError}</p>
            </div>
            <button
              onClick={() => setStatusError(null)}
              className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Back link */}
      <button
        onClick={() => {
          const returnUrl = sessionStorage.getItem('adminInvoicesReturnUrl') || '/admin/invoices';
          sessionStorage.removeItem('adminInvoicesReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        }}
        className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> Back to invoices
      </button>

      {/* Header Card */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-neutral-900 dark:text-white">
                Invoice #{invoice.invoiceNumber}
              </h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                ID: {invoice.id}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge()}
              {/* Clickable PSA Status Badge */}
              <div className="relative" ref={statusDropdownRef}>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => canApplyPayments && setShowStatusDropdown(!showStatusDropdown)}
                    disabled={!canApplyPayments || updateStatusMutation.isPending}
                    className={`px-2 py-1 text-xs font-medium rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 flex items-center gap-1 ${
                      canApplyPayments ? 'hover:bg-neutral-200 dark:hover:bg-neutral-600 cursor-pointer' : ''
                    }`}
                  >
                    {updateStatusMutation.isPending ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : null}
                    PSA: {invoice.status?.name || '-'}
                    {canApplyPayments && <ChevronDown className="w-3 h-3" />}
                  </button>
                  {/* Warning for closed invoices */}
                  {invoice.status?.name?.toLowerCase().startsWith('closed') && (
                    <div className="group relative">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      <div className="absolute right-0 top-6 w-64 p-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-xs opacity-0 group-hover:opacity-100 transition-opacity z-30 pointer-events-none">
                        Status changes on closed invoices must be made directly in ConnectWise due to API limitations.
                      </div>
                    </div>
                  )}
                </div>
                {showStatusDropdown && (
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                    {billingStatuses.map((status) => (
                      <button
                        key={status.id}
                        onClick={() => updateStatusMutation.mutate({ statusId: status.id })}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 ${
                          status.id === invoice.status?.id ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'text-neutral-700 dark:text-neutral-300'
                        }`}
                      >
                        {status.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleDownloadPdf}
                disabled={downloading}
                className="btn-secondary btn-sm"
              >
                <Download className="w-4 h-4" />
                {downloading ? 'Downloading...' : 'Download PDF'}
              </button>
              {canApplyPayments && invoice.balance > 0 && (
                <>
                  <button
                    onClick={() => setShowCreditModal(true)}
                    className="btn-secondary btn-sm"
                    title="View available credit memos (must be applied in ConnectWise)"
                  >
                    <CreditCard className="w-4 h-4" />
                    View Credits
                  </button>
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="btn-primary btn-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Apply Payment
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.summary.invoiceDate')}</p>
                <p className="font-medium dark:text-white">{formatDate(invoice.date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.summary.dueDate')}</p>
                <p className="font-medium dark:text-white">{formatDate(invoice.dueDate)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.summary.totalAmount')}</p>
                <p className="font-medium dark:text-white">{formatCurrency(invoice.total)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.summary.balanceDue')}</p>
                <p className={`font-medium ${invoice.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {formatCurrency(invoice.balance)}
                </p>
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-4">Company Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Building className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Company</p>
                  <Link
                    to={`/admin/companies/${invoice.company?.id}`}
                    className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    {invoice.company?.name || 'N/A'}
                  </Link>
                </div>
              </div>
              {invoice.billToCompany && invoice.billToCompany.id !== invoice.company?.id && (
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Billing Company</p>
                    <Link
                      to={`/admin/companies/${invoice.billToCompany.id}`}
                      className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      {invoice.billToCompany.name}
                    </Link>
                  </div>
                </div>
              )}
              {invoice.billingSiteAddressLine1 && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.billingInfo.address')}</p>
                    <p className="font-medium dark:text-white">
                      {invoice.billingSiteAddressLine1}
                      {invoice.billingSiteCity && `, ${invoice.billingSiteCity}`}
                      {invoice.billingSiteState && `, ${invoice.billingSiteState}`}
                      {invoice.billingSiteZip && ` ${invoice.billingSiteZip}`}
                    </p>
                  </div>
                </div>
              )}
              {invoice.billingTerms?.name && (
                <div className="flex items-start gap-3">
                  <Receipt className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.billingInfo.terms')}</p>
                    <p className="font-medium dark:text-white">{invoice.billingTerms.name}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Billable Items */}
      {invoice.lineItems && invoice.lineItems.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <h3 className="font-medium text-neutral-900 dark:text-white mb-4">Billable Items</h3>

          {(() => {
            const timeEntries = invoice.lineItems.filter(item => item.type === 'time');
            const otherItems = invoice.lineItems.filter(item => item.type !== 'time');

            // Group time entries by ticket
            const ticketGroups = {};
            timeEntries.forEach(entry => {
              const key = entry.ticketId || 'no-ticket';
              if (!ticketGroups[key]) {
                ticketGroups[key] = {
                  ticketId: entry.ticketId,
                  ticketSummary: entry.ticketSummary || 'Service Time',
                  entries: [],
                  totalHours: 0,
                  totalAmount: 0
                };
              }
              ticketGroups[key].entries.push(entry);
              ticketGroups[key].totalHours += entry.quantity || 0;
              ticketGroups[key].totalAmount += entry.extendedPrice || 0;
            });

            const ticketGroupArray = Object.values(ticketGroups);
            const hasTicketGroups = ticketGroupArray.length > 0;

            return (
              <>
                {/* Time Entries Grouped by Ticket */}
                {hasTicketGroups && (
                  <div className="space-y-6 mb-6">
                    {ticketGroupArray.map((group, groupIndex) => (
                      <div key={group.ticketId || groupIndex} className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
                        {/* Ticket Header */}
                        <div className="bg-neutral-50 dark:bg-neutral-700 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Ticket className="w-4 h-4 text-primary-500" />
                            <span className="font-medium text-neutral-900 dark:text-white">
                              {group.ticketSummary}
                            </span>
                            {group.ticketId && (
                              <Link
                                to={`/admin/tickets/${group.ticketId}`}
                                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                              >
                                (#{group.ticketId})
                              </Link>
                            )}
                          </div>
                          <div className="text-sm text-neutral-600 dark:text-neutral-400">
                            {group.totalHours.toFixed(2)} hrs • {formatCurrency(group.totalAmount)}
                          </div>
                        </div>

                        {/* Time Entries Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/50">
                                <th className="text-left py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">Date</th>
                                <th className="text-left py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">Member</th>
                                <th className="text-left py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">Notes</th>
                                <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">Hours</th>
                                <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">Rate</th>
                                <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.entries.map((entry, entryIndex) => (
                                <tr key={entry.id || entryIndex} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                  <td className="py-3 px-4 text-neutral-700 dark:text-neutral-300 whitespace-nowrap align-top">
                                    {entry.date ? new Date(entry.date).toLocaleDateString() : 'N/A'}
                                  </td>
                                  <td className="py-3 px-4 text-neutral-700 dark:text-neutral-300 whitespace-nowrap align-top">
                                    {entry.member || 'N/A'}
                                  </td>
                                  <td className="py-3 px-4 text-neutral-600 dark:text-neutral-400 align-top">
                                    <div className="whitespace-pre-line max-w-md">
                                      {entry.notes || '-'}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-right text-neutral-700 dark:text-neutral-300 align-top whitespace-nowrap">
                                    {entry.quantity?.toFixed(2) || '0.00'}
                                  </td>
                                  <td className="py-3 px-4 text-right text-neutral-700 dark:text-neutral-300 align-top whitespace-nowrap">
                                    {formatCurrency(entry.unitPrice || 0)}/hr
                                  </td>
                                  <td className="py-3 px-4 text-right font-medium text-neutral-900 dark:text-white align-top whitespace-nowrap">
                                    {formatCurrency(entry.extendedPrice || 0)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Products, Expenses, and Other Items */}
                {otherItems.length > 0 && (
                  <div className="overflow-x-auto">
                    {hasTicketGroups && (
                      <h4 className="font-medium text-neutral-900 dark:text-white mb-3">Products & Other Charges</h4>
                    )}
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-200 dark:border-neutral-700">
                          <th className="text-left py-2 pr-4 font-medium text-neutral-600 dark:text-neutral-400">Description</th>
                          <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">Qty</th>
                          <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">Rate</th>
                          <th className="text-right py-2 pl-4 font-medium text-neutral-600 dark:text-neutral-400">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {otherItems.map((item, index) => (
                          <tr key={item.id || index} className="border-b border-neutral-100 dark:border-neutral-800">
                            <td className="py-3 pr-4">
                              <div className="text-neutral-900 dark:text-white font-medium whitespace-pre-line">
                                {item.description || 'Item'}
                              </div>
                              {item.serialNumber && (
                                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                                  S/N: {item.serialNumber}
                                </div>
                              )}
                              {item.ticketId && (
                                <Link
                                  to={`/admin/tickets/${item.ticketId}`}
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline mt-0.5 inline-block"
                                >
                                  Ticket #{item.ticketId}
                                </Link>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right text-neutral-700 dark:text-neutral-300 align-top">
                              {item.quantity || 1}
                            </td>
                            <td className="py-3 px-4 text-right text-neutral-700 dark:text-neutral-300 align-top">
                              {formatCurrency(item.unitPrice || 0)}
                            </td>
                            <td className="py-3 pl-4 text-right font-medium text-neutral-900 dark:text-white align-top">
                              {formatCurrency(item.extendedPrice || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Invoice Breakdown */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <h3 className="font-medium text-neutral-900 dark:text-white mb-4">{d.label('invoiceDetail.section.breakdown')}</h3>
        <div className="space-y-2">
          {invoice.serviceTotal > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.serviceTotal')}</span>
              <span className="dark:text-white">{formatCurrency(invoice.serviceTotal)}</span>
            </div>
          )}
          {invoice.productTotal > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.productTotal')}</span>
              <span className="dark:text-white">{formatCurrency(invoice.productTotal)}</span>
            </div>
          )}
          {invoice.expenseTotal > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.expenseTotal')}</span>
              <span className="dark:text-white">{formatCurrency(invoice.expenseTotal)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm pt-2 border-t border-neutral-200 dark:border-neutral-700">
            <span className="font-medium dark:text-white">{d.label('invoiceDetail.breakdown.subtotal')}</span>
            <span className="font-medium dark:text-white">{formatCurrency(invoice.subtotal || invoice.total)}</span>
          </div>
          {invoice.salesTax > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.salesTax')}</span>
              <span className="dark:text-white">{formatCurrency(invoice.salesTax)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-medium dark:text-white">
            <span>{d.label('invoiceDetail.breakdown.total')}</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
          {/* Downpayment Applied */}
          {downpaymentApplied > 0 && (
            <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
              <span>Downpayment Applied</span>
              <span>-{formatCurrency(downpaymentApplied)}</span>
            </div>
          )}
          {/* Remaining Downpayment */}
          {remainingDownpayment > 0 && (
            <div className="flex justify-between text-sm text-blue-600 dark:text-blue-400">
              <span>Remaining Downpayment</span>
              <span>{formatCurrency(remainingDownpayment)}</span>
            </div>
          )}
          {invoice.payments > 0 && (
            <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
              <span>{d.label('invoiceDetail.breakdown.paymentsApplied')}</span>
              <span>-{formatCurrency(invoice.payments)}</span>
            </div>
          )}
          {(invoice.credits > 0 || creditsApplied > 0) && (
            <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
              <span className="flex items-center gap-1">
                {d.label('invoiceDetail.breakdown.creditsApplied')}
                <span
                  className="text-xs text-neutral-400 cursor-help"
                  title="ConnectWise has no API for applying credit memos — the endpoint does not exist, so no integration can do this. Apply or remove them in ConnectWise: Finance → Special Invoices → the credit memo → Apply Credit."
                >
                  (CW - read only)
                </span>
              </span>
              <span>-{formatCurrency(invoice.credits || creditsApplied)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold pt-2 border-t border-neutral-200 dark:border-neutral-700">
            <span className="dark:text-white">{d.label('invoiceDetail.breakdown.balanceDue')}</span>
            <span className={invoice.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
              {formatCurrency(invoice.balance)}
            </span>
          </div>
        </div>
      </div>

      {/* ConnectWise Payments */}
      {cwPayments.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-neutral-900 dark:text-white">ConnectWise Payments</h3>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">Can be edited/deleted</span>
          </div>
          <div className="space-y-3">
            {cwPayments.map((payment, index) => (
              <div
                key={payment.id || index}
                className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium dark:text-white">{formatCurrency(payment.amount)}</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      {payment.type?.toLowerCase().includes('credit') ? 'Credit Date' : 'Payment Date'}: {formatDate(payment.paymentDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-neutral-400 dark:text-neutral-500">ID: {payment.id}</span>
                  {canApplyPayments && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingCwPayment(payment)}
                        className="p-1.5 text-neutral-400 hover:text-primary-500 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded"
                        title="Edit payment"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeletingCwPayment(payment)}
                        className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded"
                        title="Delete payment"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Breakdown - Individual Portal Payments */}
      {paymentBreakdown.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <h3 className="font-medium text-neutral-900 dark:text-white">
            Payment Breakdown
          </h3>
          <p className="mb-4 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            ConnectWise allows one payment record per invoice, so the portal keeps each payment separately here and
            holds the ConnectWise payment equal to their total. Adding, editing or deleting one updates that total.
            A payment&rsquo;s type cannot be changed once created — that is ConnectWise, not the portal.
          </p>
          <div className="space-y-3">
            {paymentBreakdown.map((payment) => (
              <div
                key={payment.id}
                className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/50">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium dark:text-white">
                        {formatCurrency(payment.amount)}
                        {payment.refundedAmount > 0 && (
                          <span className="ml-2 text-xs font-normal text-neutral-500 dark:text-neutral-400">
                            net of {formatCurrency(payment.refundedAmount)} refunded (paid {formatCurrency(payment.originalAmount)})
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {payment.paymentType || 'Payment'}
                        {payment.reference && ` - Ref: ${payment.reference}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm text-neutral-600 dark:text-neutral-300">
                        <span className="text-neutral-500 dark:text-neutral-400">Received:</span>{' '}
                        {formatDate(payment.paymentDate || payment.createdAt)}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        <span>Entered:</span> {formatDate(payment.createdAt)}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-1 justify-end">
                        <User className="w-3 h-3" />
                        {payment.appliedByName || payment.appliedByEmail || 'Unknown'}
                      </p>
                      {payment.cwPaymentId && (
                        <p className="text-xs text-blue-500 dark:text-blue-400">
                          CW Payment #{payment.cwPaymentId} ({formatCurrency(payment.cwPaymentTotal)})
                        </p>
                      )}
                    </div>
                    {/* Edit/Delete buttons - syncs to CW */}
                    {canApplyPayments && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingBreakdown(payment)}
                          className="p-1.5 text-neutral-400 hover:text-primary-500 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded"
                          title="Edit payment (syncs to CW)"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingBreakdown(payment)}
                          className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded"
                          title="Delete payment (syncs to CW)"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {payment.notes && (
                  <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 italic">
                    {payment.notes}
                  </p>
                )}
                {refundsFor(payment.source, payment.sourcePaymentId).map((refund) => (
                  <RefundLine
                    key={refund.id}
                    refund={refund}
                    canAct={canApplyPayments}
                    onUpdate={(rid) => updateRefundCwMutation.mutate(rid)}
                    updating={updatingRefundId === refund.id}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Portal payments refunded without a breakdown row (the refund was
          not reflected in ConnectWise, so the payment was never adopted) */}
      {orphanRefundGroups.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <h3 className="font-medium text-neutral-900 dark:text-white">Refunded portal payments</h3>
          <p className="mb-4 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Payments taken through the portal and applied to this invoice, with their refunds. Net is what the customer paid less what was returned.
          </p>
          <div className="space-y-3">
            {orphanRefundGroups.map((group) => {
              const first = group[0];
              const payment = localPayments.find((p) => p.id === first.paymentId);
              const applied = first.applied ?? first.paymentAmount ?? 0;
              const refundedHere = group.reduce((s, r) => s + (r.amount || 0), 0);
              return (
                <div key={`${first.source}:${first.paymentId}`} className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/50">
                      <CreditCard className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium dark:text-white">
                        {formatCurrency(Math.max(0, applied - refundedHere))}
                        <span className="ml-2 text-xs font-normal text-neutral-500 dark:text-neutral-400">
                          net of {formatCurrency(refundedHere)} refunded (paid {formatCurrency(applied)})
                        </span>
                      </p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {first.source === 'deposit_payment' ? 'Deposit payment' : 'Portal payment'} · {formatDate(first.paidAt || payment?.created_at)}
                        {payment?.payer_name && ` · ${payment.payer_name}`}
                      </p>
                    </div>
                  </div>
                  {group.map((refund) => (
                    <RefundLine
                      key={refund.id}
                      refund={refund}
                      canAct={canApplyPayments}
                      onUpdate={(rid) => updateRefundCwMutation.mutate(rid)}
                      updating={updatingRefundId === refund.id}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {refundCwError && (
        <p className="text-sm text-red-600 dark:text-red-400">{refundCwError}</p>
      )}

      {/* View History */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
            <h3 className="font-medium text-neutral-900 dark:text-white">View History</h3>
          </div>
          {viewStats && (
            <div className="flex items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
              <span>{viewStats.totalViews} views</span>
              <span>{viewStats.uniqueSessions} sessions</span>
              <span>{viewStats.pdfDownloads} PDF downloads</span>
            </div>
          )}
        </div>

        {views.length === 0 ? (
          <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No views recorded yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                  <th className="pb-2 font-medium">Viewed At</th>
                  <th className="pb-2 font-medium">Viewer</th>
                  <th className="pb-2 font-medium">Device</th>
                  <th className="pb-2 font-medium">Browser / OS</th>
                  <th className="pb-2 font-medium">Language</th>
                  <th className="pb-2 font-medium">Engagement</th>
                  <th className="pb-2 font-medium">Session</th>
                  <th className="pb-2 font-medium">IP</th>
                  <th className="pb-2 font-medium">Referrer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {views.map((view) => (
                  <tr key={view.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                    <td className="py-3 text-neutral-900 dark:text-white whitespace-nowrap">{formatDateTime(view.viewedAt)}</td>
                    <td className="py-3">
                      <div className="text-neutral-700 dark:text-neutral-200">{view.viewerName || 'Anonymous'}</div>
                      {view.viewerEmail && (
                        <div className="text-neutral-500 dark:text-neutral-400 text-xs">{view.viewerEmail}</div>
                      )}
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        view.deviceType === 'mobile' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' :
                        view.deviceType === 'tablet' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' :
                        'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                      }`}>
                        {view.deviceType === 'mobile' && <Smartphone className="w-3 h-3 mr-1" />}
                        {view.deviceType === 'tablet' && <Tablet className="w-3 h-3 mr-1" />}
                        {view.deviceType === 'desktop' && <Monitor className="w-3 h-3 mr-1" />}
                        {view.deviceType || 'desktop'}
                      </span>
                    </td>
                    <td className="py-3 text-neutral-600 dark:text-neutral-300 text-xs">
                      <div>{view.browser} {view.browserVersion}</div>
                      <div className="text-neutral-400 dark:text-neutral-500">{view.os} {view.osVersion}</div>
                    </td>
                    <td className="py-3 text-neutral-600 dark:text-neutral-300 text-xs">
                      {view.language || '-'}
                    </td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-1">
                        {view.timeOnPage && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
                            {Math.floor(view.timeOnPage / 60)}m {view.timeOnPage % 60}s
                          </span>
                        )}
                        {view.scrolledToBottom && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                            Read all
                          </span>
                        )}
                        {view.pdfDownloaded && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                            PDF
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 text-neutral-500 dark:text-neutral-400 font-mono text-xs" title={view.sessionId || ''}>
                      {view.sessionId ? view.sessionId.substring(0, 8) + '...' : '-'}
                    </td>
                    <td className="py-3 text-neutral-500 dark:text-neutral-400 font-mono text-xs">{view.ipAddress || '-'}</td>
                    <td className="py-3 text-neutral-500 dark:text-neutral-400 text-xs max-w-[150px] truncate" title={view.referrerUrl || ''}>
                      {view.referrerUrl ? (() => { try { return new URL(view.referrerUrl).pathname; } catch { return view.referrerUrl; } })() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Apply Payment Modal */}
      {showPaymentModal && (
        <ApplyPaymentModal
          invoice={invoice}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Credit Memos Reference Modal (read-only - CW has no API for applying credits) */}
      {showCreditModal && (
        <ApplyCreditModal
          invoice={invoice}
          creditMemos={creditMemos}
          isLoading={creditMemosLoading}
          onClose={() => setShowCreditModal(false)}
        />
      )}

      {/* Edit CW Payment Modal */}
      {editingCwPayment && (
        <EditCwPaymentModal
          payment={editingCwPayment}
          onClose={() => setEditingCwPayment(null)}
          onSave={(data) => updateCwPaymentMutation.mutate({ paymentId: editingCwPayment.id, data })}
          isLoading={updateCwPaymentMutation.isPending}
          error={updateCwPaymentMutation.error?.response?.data?.error}
        />
      )}

      {/* Delete CW Payment Confirmation Modal */}
      {deletingCwPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeletingCwPayment(null)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Delete ConnectWise Payment
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Are you sure you want to delete this payment of{' '}
              <strong>{formatCurrency(deletingCwPayment.amount)}</strong> from ConnectWise?
              This action cannot be undone.
            </p>
            {deleteCwPaymentMutation.error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {deleteCwPaymentMutation.error.response?.data?.error || 'Failed to delete payment'}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingCwPayment(null)}
                disabled={deleteCwPaymentMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteCwPaymentMutation.mutate(deletingCwPayment.id)}
                disabled={deleteCwPaymentMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {deleteCwPaymentMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Payment Breakdown Modal (syncs to CW) */}
      {editingBreakdown && (
        <EditBreakdownModal
          payment={editingBreakdown}
          onClose={() => setEditingBreakdown(null)}
          onSave={(data) => updateBreakdownMutation.mutate({ breakdownId: editingBreakdown.id, data })}
          isLoading={updateBreakdownMutation.isPending}
          error={updateBreakdownMutation.error?.response?.data?.error}
        />
      )}

      {/* Delete Payment Breakdown Confirmation Modal (syncs to CW) */}
      {deletingBreakdown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeletingBreakdown(null)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Delete Payment
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-2">
              Are you sure you want to delete this payment of{' '}
              <strong>{formatCurrency(deletingBreakdown.amount)}</strong>?
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-4 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              This will update the total payment in ConnectWise automatically.
            </p>
            {deleteBreakdownMutation.error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {deleteBreakdownMutation.error.response?.data?.error || 'Failed to delete payment'}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingBreakdown(null)}
                disabled={deleteBreakdownMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteBreakdownMutation.mutate(deletingBreakdown.id)}
                disabled={deleteBreakdownMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {deleteBreakdownMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Edit CW Payment Modal Component
function EditCwPaymentModal({ payment, onClose, onSave, isLoading, error }) {
  const [amount, setAmount] = useState(payment.amount?.toString() || '');
  const [paymentDate, setPaymentDate] = useState(
    payment.paymentDate ? new Date(payment.paymentDate).toISOString().split('T')[0] : ''
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      amount: parseFloat(amount),
      paymentDate,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Edit ConnectWise Payment
          </h2>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Info note about what can be edited */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-xs">
            You can edit the amount and date. Changes are saved to ConnectWise.
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Amount
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Date
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !amount}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Credit Memo Reference Modal Component (Read-only - CW has no API for applying credits)
function ApplyCreditModal({ invoice, creditMemos, isLoading, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Available Credit Memos
          </h2>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {/* Invoice Info */}
          <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-neutral-400" />
                <span className="text-sm font-medium text-neutral-900 dark:text-white">
                  Invoice #{invoice.invoiceNumber}
                </span>
              </div>
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                Balance: {formatCurrency(invoice.balance)}
              </span>
            </div>
          </div>

          {/* Info Note - CW API Limitation */}
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-2">Credit memos must be applied in ConnectWise</p>
                <p className="mb-3">The ConnectWise API does not support applying new credit memos. You can view available credits below for reference.</p>
                <div className="bg-blue-100 dark:bg-blue-900/50 rounded p-3 text-xs">
                  <p className="font-medium mb-1">To apply a credit in ConnectWise:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Go to Finance → Special Invoices</li>
                    <li>Find and open the credit memo</li>
                    <li>Click "Apply Credit" and select the invoice</li>
                    <li>Enter the amount and save</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
              <span className="ml-2 text-neutral-500">Loading credit memos...</span>
            </div>
          ) : creditMemos.length === 0 ? (
            <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No available credit memos found for this company.</p>
              <p className="text-sm mt-1">Create a credit memo in ConnectWise first.</p>
            </div>
          ) : (
            <div>
              {/* Available Credit Memos List */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Available Credit Memos ({creditMemos.length})
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {creditMemos.map((credit) => (
                    <div
                      key={credit.id}
                      className="p-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-white">
                            Credit Memo #{credit.invoiceNumber}
                          </p>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400">
                            {formatDate(credit.date)} - {credit.status?.name || credit.status}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-green-600 dark:text-green-400">
                            {formatCurrency(Math.abs(credit.balance))}
                          </p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            Available
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Edit Payment Breakdown Modal Component (syncs to CW)
function EditBreakdownModal({ payment, onClose, onSave, isLoading, error }) {
  const [amount, setAmount] = useState(payment.amount?.toString() || '');
  const [paymentDate, setPaymentDate] = useState(
    payment.paymentDate ? new Date(payment.paymentDate).toISOString().split('T')[0] : ''
  );
  const [reference, setReference] = useState(payment.reference || '');
  const [notes, setNotes] = useState(payment.notes || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      amount: parseFloat(amount),
      paymentDate,
      reference,
      notes,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Edit Payment
          </h2>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* CW Sync notice */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Changes will update the total payment in ConnectWise.
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payment Date</label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Reference / Transaction ID</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              placeholder="e.g., Check #12345"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
